// src/store/holidaySlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// ✅ Correct API base
const API_BASE = 'https//api.monkeybox.ai/api/holidays';

// ✅ Fetch holidays
export const fetchHolidays = createAsyncThunk(
  'holidays/fetchHolidays',
  async ({ year, month, schoolId }, { rejectWithValue }) => {
    try {
      const params = { year };
      if (month) params.month = month;
      if (schoolId) params.schoolId = schoolId;

      const response = await axios.get(`${API_BASE}/search`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch holidays');
    }
  }
);

// ✅ Create multiple holidays
export const createHoliday = createAsyncThunk(
  'holidays/createHoliday',
  async (holidayList, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE}`, holidayList);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create holiday');
    }
  }
);

// ✅ Update a holiday by ID
export const updateHoliday = createAsyncThunk(
  'holidays/updateHoliday',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE}/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update holiday');
    }
  }
);

// ✅ Delete a holiday by ID
export const deleteHoliday = createAsyncThunk(
  'holidays/deleteHoliday',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_BASE}/${id}`);
      return id; // Return id to remove from local store
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete holiday');
    }
  }
);

// ✅ Slice
const holidaySlice = createSlice({
  name: 'holidays',
  initialState: {
    holidays: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearHolidays(state) {
      state.holidays = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔵 FETCH
      .addCase(fetchHolidays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.loading = false;
        state.holidays = action.payload;
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🟢 CREATE
      .addCase(createHoliday.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHoliday.fulfilled, (state, action) => {
        state.loading = false;
        state.holidays.push(...action.payload); // Assuming backend returns array
      })
      .addCase(createHoliday.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🟡 UPDATE
      .addCase(updateHoliday.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHoliday.fulfilled, (state, action) => {
        state.loading = false;
        const updatedHoliday = action.payload;
        const index = state.holidays.findIndex(h => h.id === updatedHoliday.id);
        if (index !== -1) {
          state.holidays[index] = updatedHoliday;
        }
      })
      .addCase(updateHoliday.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔴 DELETE
      .addCase(deleteHoliday.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHoliday.fulfilled, (state, action) => {
        state.loading = false;
        const id = action.payload;
        state.holidays = state.holidays.filter(h => h.id !== id);
      })
      .addCase(deleteHoliday.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearHolidays } = holidaySlice.actions;
export default holidaySlice.reducer;
